import React from "react";

import {
  FaArrowRight,
  FaBell,
  FaClock,
  FaExpand,
  FaFlagCheckered,
  FaLaptop,
  FaPlay,
  FaRedo,
  FaStopwatch,
  FaTabletAlt,
  FaTrophy,
  FaUsers,
} from "react-icons/fa";

import ScoreButton from "../ui/ScoreButton";

const FEATURES = [
  {
    icon: FaTrophy,
    title: "Scores de judo",
    text: "Ajoutez ou retirez facilement les Ippon, Waza-ari et Yuko pour chaque combattant.",
  },
  {
    icon: FaClock,
    title: "Chronomètre complet",
    text: "Démarrez, arrêtez ou ajustez le temps de combat directement depuis l’écran.",
  },
  {
    icon: FaStopwatch,
    title: "Gestion de l’Osaekomi",
    text: "Suivez automatiquement la durée de l’immobilisation et l’attribution des valeurs.",
  },
  {
    icon: FaFlagCheckered,
    title: "Shido et résultat",
    text: "Gérez les pénalités, le Hansoku-make et la désignation du vainqueur.",
  },
  {
    icon: FaExpand,
    title: "Affichage plein écran",
    text: "Projetez le tableau sur une télévision, un écran ou un vidéoprojecteur.",
  },
  {
    icon: FaBell,
    title: "Alertes sonores",
    text: "Un signal sonore indique la fin du temps de combat et les moments importants.",
  },
];

const USE_CASES = [
  {
    icon: FaUsers,
    title: "Cours au club",
    text: "Mettez vos judokas en situation réelle pendant les randoris et travaillez les règles d’arbitrage.",
  },
  {
    icon: FaTrophy,
    title: "Animations",
    text: "Organisez facilement un challenge interne, une animation parents-enfants ou une rencontre amicale.",
  },
  {
    icon: FaStopwatch,
    title: "Stages",
    text: "Utilisez le tableau pendant un stage sportif ou une séance consacrée à l’arbitrage.",
  },
  {
    icon: FaLaptop,
    title: "Petites compétitions",
    text: "Disposez rapidement d’un affichage clair sans installer un logiciel complexe.",
  },
];

const STEPS = [
  {
    number: "01",
    title: "Configurez le combat",
    text: "Choisissez la durée, le numéro du tapis, les règles de fin et les noms des judokas.",
  },
  {
    number: "02",
    title: "Affichez le tableau",
    text: "Ouvrez MonAppliScore sur votre ordinateur, votre tablette ou un écran dédié.",
  },
  {
    number: "03",
    title: "Pilotez le combat",
    text: "Gérez le chronomètre, les scores, les pénalités et l’Osaekomi en temps réel.",
  },
];

export default function ScorePresentation({
  onOpenScoreboard,
}) {
  return (
    <div
      id="presentation-monappliscore"
      className="scroll-mt-24"
    >
      <PreviewSection
        onOpenScoreboard={
          onOpenScoreboard
        }
      />

      <FeaturesSection />

      <StepsSection />

      <UseCasesSection />

      <FinalCallToAction
        onOpenScoreboard={
          onOpenScoreboard
        }
      />
    </div>
  );
}

function PreviewSection({
  onOpenScoreboard,
}) {
  return (
    <section className="py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
          <div className="relative">
            <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-orange-500/15 to-blue-600/15 blur-2xl" />

            <div className="relative overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-950 p-2 shadow-2xl shadow-slate-950/15 dark:border-slate-800">
              <img
                src="/images/appli/monappliscore2.webp"
                alt="Tableau de score de judo MonAppliScore"
                className="aspect-[16/10] w-full rounded-[1.6rem] bg-black object-contain"
                loading="lazy"
              />
            </div>

            <div className="absolute -bottom-5 left-5 right-5 flex flex-col gap-2 rounded-2xl border border-white/70 bg-white/95 p-4 shadow-xl backdrop-blur dark:border-slate-700 dark:bg-slate-900/95 sm:left-auto sm:right-6 sm:max-w-xs">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-orange-600">
                Utilisation intuitive
              </p>

              <p className="text-sm font-black text-slate-950 dark:text-white">
                Un clic pour ajouter, un appui
                long pour retirer.
              </p>
            </div>
          </div>

          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-orange-600 dark:text-orange-300">
              Simple à utiliser
            </p>

            <h2 className="mt-4 text-3xl font-black tracking-tight text-slate-950 dark:text-white sm:text-4xl lg:text-5xl">
              Un véritable tableau de score,
              directement dans votre navigateur
            </h2>

            <p className="mt-6 text-base leading-8 text-slate-600 dark:text-slate-300">
              MonAppliScore a été pensé pour
              permettre aux enseignants,
              arbitres bénévoles et dirigeants
              de lancer rapidement un combat,
              sans créer une compétition
              complète.
            </p>

            <p className="mt-4 text-base leading-8 text-slate-600 dark:text-slate-300">
              L’interface privilégie la
              lisibilité et les grandes zones
              tactiles afin de rester
              facilement utilisable depuis un
              ordinateur ou une tablette.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <InfoPill
                icon={FaPlay}
                text="Lancement immédiat"
              />

              <InfoPill
                icon={FaTabletAlt}
                text="Compatible tablette"
              />

              <InfoPill
                icon={FaRedo}
                text="Réinitialisation rapide"
              />
            </div>

            <ScoreButton
              icon={FaArrowRight}
              variant="dark"
              className="mt-9"
              onClick={
                onOpenScoreboard
              }
            >
              Essayer MonAppliScore
            </ScoreButton>
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoPill({
  icon: Icon,
  text,
}) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-xs font-bold text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
      <Icon className="text-orange-500" />

      {text}
    </span>
  );
}

function FeaturesSection() {
  return (
    <section className="border-y border-slate-200 bg-white py-16 dark:border-slate-800 dark:bg-slate-900/50 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-orange-600 dark:text-orange-300">
            Toutes les fonctions essentielles
          </p>

          <h2 className="mt-4 text-3xl font-black text-slate-950 dark:text-white sm:text-4xl">
            Concentrez-vous sur le combat
          </h2>

          <p className="mt-4 text-sm leading-7 text-slate-500 dark:text-slate-400 sm:text-base">
            Les principales commandes sont
            accessibles directement depuis le
            tableau, avec un affichage adapté
            aux salles de sport.
          </p>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(
            ({
              icon: Icon,
              title,
              text,
            }) => (
              <article
                key={title}
                className="group rounded-3xl border border-slate-200 bg-slate-50 p-6 transition duration-300 hover:-translate-y-1 hover:border-orange-300 hover:bg-white hover:shadow-xl dark:border-slate-800 dark:bg-slate-950/70 dark:hover:border-orange-500/50 dark:hover:bg-slate-900"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#004b86] text-xl text-white shadow-lg shadow-blue-950/20 transition group-hover:bg-orange-500">
                  <Icon />
                </div>

                <h3 className="mt-5 text-lg font-black text-slate-950 dark:text-white">
                  {title}
                </h3>

                <p className="mt-3 text-sm leading-7 text-slate-500 dark:text-slate-400">
                  {text}
                </p>
              </article>
            )
          )}
        </div>
      </div>
    </section>
  );
}

function StepsSection() {
  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.75fr_1.25fr] lg:items-start">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.2em] text-orange-600 dark:text-orange-300">
              Prêt en quelques secondes
            </p>

            <h2 className="mt-4 text-3xl font-black text-slate-950 dark:text-white sm:text-4xl">
              Comment utiliser
              MonAppliScore ?
            </h2>

            <p className="mt-5 text-sm leading-7 text-slate-500 dark:text-slate-400 sm:text-base">
              Aucun paramétrage complexe :
              vous choisissez les informations
              du combat et le tableau est
              immédiatement utilisable.
            </p>
          </div>

          <div className="grid gap-4">
            {STEPS.map(
              ({
                number,
                title,
                text,
              }) => (
                <article
                  key={number}
                  className="flex gap-5 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6"
                >
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-orange-500 text-lg font-black text-white shadow-lg shadow-orange-950/20">
                    {number}
                  </div>

                  <div>
                    <h3 className="text-lg font-black text-slate-950 dark:text-white">
                      {title}
                    </h3>

                    <p className="mt-2 text-sm leading-7 text-slate-500 dark:text-slate-400">
                      {text}
                    </p>
                  </div>
                </article>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function UseCasesSection() {
  return (
    <section className="relative overflow-hidden bg-slate-950 py-16 text-white sm:py-20">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(249,115,22,0.18),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(0,75,134,0.28),transparent_35%)]" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-xs font-black uppercase tracking-[0.2em] text-orange-300">
            Pour tous les moments du club
          </p>

          <h2 className="mt-4 text-3xl font-black sm:text-4xl">
            Un tableau pour vos cours,
            stages et animations
          </h2>

          <p className="mt-4 text-sm leading-7 text-slate-400 sm:text-base">
            MonAppliScore peut être utilisé
            aussi bien pendant un cours
            classique que lors d’une animation
            ou d’une rencontre sportive.
          </p>
        </div>

        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {USE_CASES.map(
            ({
              icon: Icon,
              title,
              text,
            }) => (
              <article
                key={title}
                className="rounded-3xl border border-white/10 bg-white/[0.07] p-6 backdrop-blur"
              >
                <Icon className="text-2xl text-orange-300" />

                <h3 className="mt-5 text-lg font-black">
                  {title}
                </h3>

                <p className="mt-3 text-sm leading-7 text-slate-400">
                  {text}
                </p>
              </article>
            )
          )}
        </div>
      </div>
    </section>
  );
}

function FinalCallToAction({
  onOpenScoreboard,
}) {
  return (
    <section className="py-16 sm:py-20 lg:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-[2rem] bg-[#004b86] px-6 py-12 text-white shadow-2xl shadow-blue-950/20 sm:px-10 lg:px-14">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(249,115,22,0.45),transparent_35%)]" />

          <div className="absolute -bottom-32 -left-20 h-72 w-72 rounded-full bg-white/10 blur-3xl" />

          <div className="relative flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-3xl">
              <span className="inline-flex rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.18em]">
                Gratuit et sans engagement
              </span>

              <h2 className="mt-5 text-3xl font-black sm:text-4xl lg:text-5xl">
                Prêt à lancer votre premier
                combat ?
              </h2>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-blue-100 sm:text-base">
                Configurez les judokas et la
                durée du combat, puis affichez
                immédiatement votre tableau de
                score.
              </p>
            </div>

            <ScoreButton
              icon={FaArrowRight}
              onClick={
                onOpenScoreboard
              }
              className="shrink-0 bg-white text-[#004b86] hover:bg-orange-50"
            >
              Ouvrir gratuitement
            </ScoreButton>
          </div>
        </div>
      </div>
    </section>
  );
}